"use client";

import { AgentThreadVoStatus } from "@/api";
import StarIcon from "@/assets/icons/star.svg";
import { useConfirmBeforeSend } from "@/hooks/useConfirmBeforeSend";
import { useSendMessage } from "@/hooks/useSendMessage";
import { PlusOutlined } from "@ant-design/icons";
import "@ant-design/v5-patch-for-react-19";
import { Button, Input, Switch, Upload } from "antd";
import classNames from "classnames";
import { useRef, useState } from "react";
import { ChatBoxFiles } from "../ChatBoxFiles";
import { Confirm } from "../Confirm";
import { Tooltip } from "../Tooltip";
import { Border } from "./Border";
import styles from "./index.module.css";

const { TextArea } = Input;

interface ChatBoxProps {
  placeholder?: string;
  disabled?: boolean;
  bigUploadButton?: boolean;
  threadId?: string;
  threadStatus?: AgentThreadVoStatus;
}

export function ChatBox({
  placeholder = "Describe your idea, paste a link or upload your product...",
  disabled = false,
  bigUploadButton = true,
}: ChatBoxProps) {
  const chatBoxRef = useRef<HTMLDivElement>(null);
  const {
    text,
    handleMessageTextChange,
    handleSendMessage,
    attachmentList,
    textAreaRef,
    uploadProps,
    onRemove,
    isSending,
    isAuto,
    handleAutoChange,
    isAutoEnabled,
  } = useSendMessage(undefined, chatBoxRef);
  const [isFocused, setIsFocused] = useState(false);
  const [isHover, setIsHover] = useState(false);

  const hasAttachments = attachmentList.length > 0;
  const useBigUploadButton = !hasAttachments && bigUploadButton;

  const {
    confirmOpen,
    setConfirmOpen,
    handleKeyDown: handleTextAreaKeyDown,
    handleConfirmSend,
  } = useConfirmBeforeSend(hasAttachments, handleSendMessage);

  const active = isFocused || isHover;

  return (
    <div
      ref={chatBoxRef}
      className={styles.wrapper}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      {active && (
        <Border className="w-full h-full absolute top-0 left-0 z-2 pointer-events-none" />
      )}

      <div className={classNames(styles.inner, { [styles.active]: active })}>
        {/* 文件列表显示 */}
        {attachmentList.length > 0 && (
          <div className="px-[24px] py-[20px] pb-2">
            <ChatBoxFiles attachmentList={attachmentList} onRemove={onRemove} />
          </div>
        )}

        {/* 主输入区域 */}
        <div className="">
          <TextArea
            autoFocus={true}
            ref={textAreaRef}
            value={text}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onChange={(e) => handleMessageTextChange(e.target.value)}
            onKeyDown={handleTextAreaKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            autoSize={{ minRows: 1, maxRows: 5 }}
            className={classNames(styles.textarea, {
              "!pt-0": attachmentList.length > 0,
            })}
          />

          {/* 底部工具栏 */}
          <div
            className={classNames(styles.bottom, {
              [styles.bottomWithFiles]: !useBigUploadButton,
            })}
          >
            {/* 左侧：文件上传按钮 */}
            <div className="flex items-center gap-2">
              <Upload
                {...uploadProps}
                className="!leading-0"
                disabled={disabled}
              >
                {useBigUploadButton ? (
                  <div className={styles.uploadButton}>
                    <Tooltip title="Upload your image or video files">
                      <div className={styles.uploadButtonInner}>
                        <PlusOutlined className="text-[24px] mb-2 text-[#4D596C]" />
                        <div className="text-xs text-center font-medium">
                          Upload media
                        </div>
                      </div>
                    </Tooltip>
                  </div>
                ) : (
                  <div className={styles.smallUploadButton}>
                    <PlusOutlined />
                  </div>
                )}
              </Upload>
            </div>

            {/* 右侧：发送按钮和快捷键提示 */}
            <div
              className={classNames("flex items-end  justify-end gap-2", {
                "!h-[100px] pb-[20px]": useBigUploadButton,
              })}
            >
              <div className="flex items-center gap-[20px]">
                {isAutoEnabled && (
                  <div>
                    <span className="mr-2 font-medium">Auto</span>
                    <Switch
                      checked={isAuto}
                      disabled={disabled}
                      onChange={handleAutoChange}
                    />
                  </div>
                )}
                <Confirm
                  title="Using content you don't own may violate copyright laws. By continuing, you confirm this is your own original content."
                  open={confirmOpen}
                  onOpenChange={setConfirmOpen}
                  onConfirm={handleConfirmSend}
                  skipConfirm={!hasAttachments}
                  disabled={isSending}
                >
                  <Button
                    size="large"
                    type="primary"
                    className="flex items-center !w-[120px] !h-[40px] !font-medium"
                    loading={isSending}
                    disabled={isSending}
                    icon={
                      !isSending && <StarIcon className="w-4 h-4 text-white" />
                    }
                  >
                    Submit
                  </Button>
                </Confirm>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
